/**
 * Invite people to a room, as the confirmed design draws it: one way of choosing who, picked like a radio, the
 * count the server works out, the room's own link to share, and Send invites. The email the server writes first
 * can be changed, tried on yourself, or written again. The server decides who is counted (and who is left out on
 * their own); the sheet shows its answer as the host chooses.
 */
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { INVITE_SYSTEM_TAGS, parsePasted } from "../../shared/invites";
import { LIVE_LENGTHS } from "../../shared/live-entry";
import { labelParts, liveLongLabel } from "../../shared/live-time";
import { fillText, namesFrom } from "../../shared/tokens";
import type { InviteAudience, InviteOptions, InvitePreview, InviteSendView, InviteSkip, RegistrantSummary } from "../../shared/types";
import { ApiError, api } from "../lib/api";
import { appPath } from "../lib/workspace";
import { Button, IconButton } from "./ui/Button";
import { Field } from "./ui/Field";
import { Icon } from "./ui/Icon";
import { Select } from "./ui/Select";
import { Sheet } from "./ui/Sheet";
import "../styles/invite.css";

/** The room an invite is for, as the sheet shows it. */
export interface InviteTarget {
  id: string;
  title: string;
  /**
   * "Thursday, October 1 · 7:00 PM ET" for a live; for a series its rhythm and
   * the next session, "Every Thursday · 7:00 PM ET, next Thu, Oct 1"; empty for
   * a talk people watch any time.
   */
  whenLong: string;
  /** "60 min", beside the time. */
  length: string;
  /** The room's public link, for the host to share themselves; absent when the sheet has none to hand. */
  link?: string;
}

/** The time an invite says: a one-off's day and time, or a series' rhythm and its next session. */
function inviteWhen(live: { startsAt: string; timezone: string; series?: { chip: string; long?: string } }): string {
  if (!live.series) return liveLongLabel(live.startsAt, live.timezone);
  const p = labelParts(live.startsAt, live.timezone, "short");
  const rhythm = live.series.long ?? live.series.chip;
  return p ? `${rhythm}, next ${p.weekday}, ${p.month} ${p.day}` : rhythm;
}

/** A room as the sheet shows it, from the list's card or the room's page. */
export function inviteTargetOf(w: {
  id: string;
  title: string;
  register_url?: string;
  live?: { startsAt: string; timezone: string; lengthMin: number; series?: { chip: string; long?: string } } | null;
}): InviteTarget {
  const live = w.live ?? null;
  return {
    id: w.id,
    title: w.title,
    whenLong: live ? inviteWhen(live) : "",
    length: live ? (LIVE_LENGTHS.find((l) => l.min === live.lengthMin)?.label ?? `${live.lengthMin} min`) : "",
    ...(w.register_url ? { link: w.register_url } : {}),
  };
}

/**
 * Can people still be invited? A published page; for a one-off live, one that has not started or one that has
 * ended (the invite is to its replay); for a series, one with sessions still to come.
 */
export function invitable(w: { status: string; live?: { state: string; series?: { ended: boolean } } | null }): boolean {
  if (w.status !== "published") return false;
  if (!w.live) return true;
  return w.live.series ? !w.live.series.ended : w.live.state === "scheduled" || w.live.state === "ended";
}

type Kind = "webinars" | "tags" | "people" | "added" | "everyone";
type View = "who" | "email";

const KIND: Record<Kind, string> = {
  webinars: "People from your other rooms",
  tags: "People with a tag",
  people: "People you pick",
  added: "Type or paste addresses",
  everyone: "Everyone in People",
};
const ORDER: Kind[] = ["webinars", "tags", "people", "added", "everyone"];

const SKIP_LABEL: Record<InviteSkip, string> = {
  seat: "already have a seat",
  invited: "already invited",
  unsubscribed: "unsubscribed",
  bounced: "bounced before",
};

const fmt = (n: number): string => n.toLocaleString("en-US");
const plural = (n: number, one: string, many: string): string => `${fmt(n)} ${n === 1 ? one : many}`;

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor">
      {children}
    </svg>
  );
}
const IconX = () => (<Svg><path d="M6 6l12 12M18 6L6 18" /></Svg>);
const IconTick = () => (<Svg><path d="M5 12l5 5 9-10" /></Svg>);
const IconSpark = () => (<Svg><path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z" /></Svg>);
const IconSearch = () => (<Svg><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></Svg>);

function Check({ on }: { on: boolean }) {
  return <span className={on ? "inv-cbx on" : "inv-cbx"} aria-hidden="true"><IconTick /></span>;
}

function toggle(list: string[], v: string): string[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

/** Under each way of choosing, how many it could reach before anything is picked. */
function kindCount(kind: Kind, options: InviteOptions): string {
  if (kind === "webinars") {
    const n = options.webinars.reduce((a, w) => a + w.people, 0);
    return options.webinars.length ? plural(n, "person", "people") : "Your other rooms have nobody yet";
  }
  if (kind === "tags") return options.tags.length ? plural(options.tags.length, "tag", "tags") : "You have no tags of your own yet";
  if (kind === "everyone") return plural(options.everyone, "person", "people");
  return "";
}

// ── the sheet ─────────────────────────────────────────────────────────────
export function InviteSheet({
  webinar,
  choices,
  picked,
  onClose,
  onSent,
  onToast,
}: {
  /** The room, when the sheet opens from it. */
  webinar?: InviteTarget;
  /** The rooms to choose from, when it opens from People. */
  choices?: InviteTarget[];
  /** People already picked in People. */
  picked?: Array<{ email: string; name: string }>;
  onClose: () => void;
  onSent: (send: InviteSendView, webinar: InviteTarget) => void;
  onToast: (message: string) => void;
}) {
  const [target, setTarget] = useState<InviteTarget | null>(webinar ?? choices?.[0] ?? null);
  const [options, setOptions] = useState<InviteOptions | null>(null);
  const [loadError, setLoadError] = useState("");
  const [view, setView] = useState<View>("who");
  const [kind, setKind] = useState<Kind | null>(picked?.length ? "people" : null);
  const [webinarIds, setWebinarIds] = useState<string[]>([]);
  const [webinarTags, setWebinarTags] = useState<string[]>(["watched"]);
  const [tags, setTags] = useState<string[]>([]);
  const [people, setPeople] = useState<Array<{ email: string; name: string }>>(picked ?? []);
  const [added, setAdded] = useState("");
  const [consent, setConsent] = useState(false);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"" | "send" | "test" | "draft">("");
  const [error, setError] = useState("");

  // ── the options, for whichever room the invite is to
  useEffect(() => {
    if (!target) return;
    let alive = true;
    setOptions(null);
    setLoadError("");
    api.invites
      .options(target.id)
      .then((o) => {
        if (!alive) return;
        setOptions(o);
        setSubject(o.draft.subject);
        setNote(o.draft.note);
        setWebinarIds((ids) => (ids.length ? ids.filter((id) => o.webinars.some((w) => w.id === id)) : o.webinars.slice(0, 1).map((w) => w.id)));
      })
      .catch((err) => alive && setLoadError(err instanceof ApiError ? err.message : "We couldn't load this. Try again."));
    return () => {
      alive = false;
    };
  }, [target]);

  const audience = useMemo<InviteAudience>(
    () => ({
      people: kind === "people" ? people.map((p) => p.email) : [],
      webinars: kind === "webinars" ? { ids: webinarIds, tags: webinarTags } : null,
      tags: kind === "tags" ? tags : [],
      everyone: kind === "everyone",
      added: kind === "added" ? added : "",
    }),
    [kind, people, webinarIds, webinarTags, tags, added],
  );

  // ── the count, from the server, a moment after the host stops changing things
  useEffect(() => {
    if (!target || !options || !kind) {
      setPreview(null);
      return;
    }
    const ctl = new AbortController();
    const t = window.setTimeout(() => {
      api.invites
        .preview(target.id, audience, { signal: ctl.signal })
        .then(setPreview)
        .catch(() => undefined);
    }, 220);
    return () => {
      window.clearTimeout(t);
      ctl.abort();
    };
  }, [target, options, audience, kind]);

  const pasted = useMemo(() => parsePasted(added), [added]);
  const freshCount = preview?.added.fresh.length ?? 0;
  const needsConsent = kind === "added" && freshCount > 0;
  const total = preview?.total ?? 0;
  const sendingOn = options?.sending.on ?? false;
  const canSend = sendingOn && total > 0 && (!needsConsent || consent) && busy === "";

  const send = async () => {
    if (!target || !canSend) return;
    setBusy("send");
    setError("");
    try {
      const res = await api.invites.send(target.id, { audience, subject, note, consent });
      onSent(res.send, target);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The invites didn't go out. Try again.");
    } finally {
      setBusy("");
    }
  };

  const test = async () => {
    if (!target || busy) return;
    setBusy("test");
    setError("");
    try {
      const res = await api.invites.test(target.id, { subject, note });
      onToast(`A test is on its way to ${res.to}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The test didn't send. Try again.");
    } finally {
      setBusy("");
    }
  };

  const rewrite = async () => {
    if (!target || busy) return;
    setBusy("draft");
    setError("");
    try {
      const d = await api.invites.draft(target.id, { subject, note });
      setSubject(d.subject);
      setNote(d.note);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We couldn't write it again just now. Try again.");
    } finally {
      setBusy("");
    }
  };

  const copyLink = (link: string) =>
    void navigator.clipboard.writeText(link).then(
      () => onToast("Link copied"),
      () => onToast("We couldn't copy the link"),
    );

  const first = preview?.people[0];
  const sample = namesFrom(first?.name ?? "").firstName || "there";
  const skipped = preview
    ? (Object.keys(SKIP_LABEL) as InviteSkip[]).filter((k) => preview.skipped[k] > 0).map((k) => `${fmt(preview.skipped[k])} ${SKIP_LABEL[k]}`)
    : [];
  const sub = target ? [target.title, target.whenLong].filter(Boolean).join(" · ") : undefined;
  const sendButton = (
    <Button variant="p" icon="send" disabled={!canSend} onClick={() => void send()}>
      {busy === "send" ? "Sending…" : total > 0 ? `Send ${plural(total, "invite", "invites")}` : "Send invites"}
    </Button>
  );

  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title={view === "who" ? "Invite people" : "The email"}
      sub={sub}
      footer={
        view === "who" ? (
          <>
            {options ? (
              <Button variant="q" icon="mail" className="inv-emailbtn" onClick={() => setView("email")}>
                The email
              </Button>
            ) : null}
            <Button onClick={onClose}>Cancel</Button>
            {sendButton}
          </>
        ) : (
          <>
            <Button variant="q" icon="left" className="inv-emailbtn" onClick={() => setView("who")}>
              Who gets it
            </Button>
            <Button disabled={!sendingOn || busy !== ""} onClick={() => void test()}>
              {busy === "test" ? "Sending a test…" : "Send me a test"}
            </Button>
            {sendButton}
          </>
        )
      }
    >
      <div className="col inv-b" style={{ gap: 10 }}>
        {choices && choices.length > 1 ? (
          <Field label="The room">
            <Select
              ariaLabel="The room"
              value={target?.id ?? ""}
              options={choices.map((w) => ({ value: w.id, label: w.title }))}
              onChange={(id) => setTarget(choices.find((w) => w.id === id) ?? null)}
            />
          </Field>
        ) : null}
        {options && !sendingOn ? (
          <div className="strip warn">
            <div className="ic">
              <Icon name="mail" />
            </div>
            <div className="sp">
              <b>Your emails need a sending address first.</b>
            </div>
            <a className="btn s" href={appPath("/setup/email")}>
              Set one up
            </a>
          </div>
        ) : null}
        {!target ? (
          <p className="small muted">Publish a room first, then invite people to it.</p>
        ) : loadError ? (
          <p className="small muted">{loadError}</p>
        ) : !options ? (
          <div className="sk" style={{ height: 220, borderRadius: 12 }} />
        ) : view === "who" ? (
          <>
            {ORDER.map((k) => {
              const on = kind === k;
              const count = kindCount(k, options);
              return (
                <div
                  key={k}
                  className={on ? "radio on" : "radio"}
                  role="radio"
                  aria-checked={on}
                  tabIndex={0}
                  onClick={() => setKind(k)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                      e.preventDefault();
                      setKind(k);
                    }
                  }}
                >
                  <span className="rd" />
                  <div className="sp inv-way">
                    <div className="b7">{KIND[k]}</div>
                    {count ? <div className="small muted">{count}</div> : null}
                    {on ? (
                      <div className="inv-part">
                        {k === "webinars" ? (
                          <WebinarsPart options={options} ids={webinarIds} setIds={setWebinarIds} tags={webinarTags} setTags={setWebinarTags} counts={preview?.webinar_tags ?? {}} />
                        ) : k === "tags" ? (
                          <TagsPart options={options} tags={tags} setTags={setTags} />
                        ) : k === "people" ? (
                          <PeoplePart people={people} setPeople={setPeople} />
                        ) : k === "added" ? (
                          <AddedPart text={added} setText={setAdded} preview={preview} bad={pasted.bad} consent={consent} setConsent={setConsent} />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {kind && preview ? (
              <p className="small inv-count">
                <b>{plural(total, "person gets", "people get")} their own email and link.</b>
                {skipped.length ? ` Left out on their own: ${skipped.join(", ")}.` : ""}
              </p>
            ) : null}
            {target.link ? (
              <div className="field mt8">
                <div className="lbl">Or share the link yourself</div>
                <div className="copyv">
                  <span>{target.link.replace(/^https?:\/\//, "")}</span>
                  <IconButton icon="copy" label="Copy link" small onClick={() => copyLink(target.link ?? "")} />
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <Field label="Subject" htmlFor="inv-subj">
              <input dir="auto" id="inv-subj" className="inp" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
            </Field>
            <Field label="Your note" htmlFor="inv-note" hint="Above the time and the button.">
              <textarea dir="auto" id="inv-note" className="inp" value={note} rows={8} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <TokenRow onPut={(tok) => setNote((n) => `${n}${n.endsWith(" ") || n.endsWith("\n") || !n ? "" : " "}${tok}`)} busy={busy === "draft"} onRewrite={() => void rewrite()} />
            <div className="inv-pv">
              <p className="inv-pvlabel">
                <span>What {sample === "there" ? "they see" : `${sample} sees`}</span>
                <span>{plural(total, "person", "people")}</span>
              </p>
              <EmailPreview subject={subject} note={note} first={sample} webinar={target} fromName={options.sending.from_name} />
            </div>
          </>
        )}
        {error ? (
          <p className="small inv-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

function WebinarsPart({
  options,
  ids,
  setIds,
  tags,
  setTags,
  counts,
}: {
  options: InviteOptions;
  ids: string[];
  setIds: (f: (ids: string[]) => string[]) => void;
  tags: string[];
  setTags: (f: (tags: string[]) => string[]) => void;
  counts: Record<string, number>;
}) {
  if (!options.webinars.length) return <p className="inv-quiet">Your other rooms have nobody registered yet.</p>;
  return (
    <>
      <div className="inv-opts">
        {options.webinars.map((w) => {
          const on = ids.includes(w.id);
          return (
            <button type="button" key={w.id} className={on ? "inv-opt on" : "inv-opt"} aria-pressed={on} onClick={() => setIds((l) => toggle(l, w.id))}>
              <Check on={on} />
              <span>
                <b dir="auto">{w.title}</b>
                <small>{w.when}</small>
              </span>
              <span className="inv-c">{fmt(w.people)}</span>
            </button>
          );
        })}
      </div>
      <div className="inv-tagrow">
        <span className="inv-rowlb">Who has any of</span>
        {INVITE_SYSTEM_TAGS.map((t) => {
          const on = tags.includes(t.name);
          return (
            <button
              type="button"
              key={t.name}
              className={on ? "inv-tag sys on" : "inv-tag sys"}
              aria-pressed={on}
              title={`Flashroom adds it ${t.when}`}
              onClick={() => setTags((l) => toggle(l, t.name))}
            >
              {t.name}
              {ids.length ? <span className="inv-c">{fmt(counts[t.name] ?? 0)}</span> : null}
            </button>
          );
        })}
      </div>
      {ids.length && !tags.length ? <p className="inv-quiet">Pick at least one tag.</p> : null}
    </>
  );
}

function TagsPart({ options, tags, setTags }: { options: InviteOptions; tags: string[]; setTags: (f: (t: string[]) => string[]) => void }) {
  if (!options.tags.length) return <p className="inv-quiet">You have no tags of your own yet.</p>;
  return (
    <div className="inv-tagrow">
      {options.tags.map((t) => {
        const on = tags.includes(t.name);
        return (
          <button type="button" key={t.name} className={on ? "inv-tag own on" : "inv-tag own"} aria-pressed={on} onClick={() => setTags((l) => toggle(l, t.name))}>
            {t.name}
            <span className="inv-c">{fmt(t.people)}</span>
          </button>
        );
      })}
    </div>
  );
}

function PeoplePart({
  people,
  setPeople,
}: {
  people: Array<{ email: string; name: string }>;
  setPeople: (f: (p: Array<{ email: string; name: string }>) => Array<{ email: string; name: string }>) => void;
}) {
  const [all, setAll] = useState<RegistrantSummary[] | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    let alive = true;
    api.people
      .list()
      .then((d) => {
        if (alive) setAll(d.people);
      })
      .catch(() => alive && setAll([]));
    return () => {
      alive = false;
    };
  }, []);
  const needle = q.trim().toLowerCase();
  const shown = (all ?? []).filter((p) => !needle || `${p.name} ${p.email}`.toLowerCase().includes(needle)).slice(0, 60);
  const chosen = new Set(people.map((p) => p.email.toLowerCase()));
  const flip = (p: { email: string; name: string }) =>
    setPeople((list) =>
      list.some((x) => x.email.toLowerCase() === p.email.toLowerCase())
        ? list.filter((x) => x.email.toLowerCase() !== p.email.toLowerCase())
        : [...list, { email: p.email, name: p.name }],
    );
  return (
    <>
      {people.length ? (
        <div className="inv-picked">
          {people.map((p) => (
            <span className="inv-pchip" key={p.email}>
              {p.name || p.email}
              <button type="button" aria-label={`Take ${p.name || p.email} off`} onClick={() => flip(p)}>
                <IconX />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <label className="inv-search">
        <IconSearch />
        <input dir="auto" value={q} placeholder="Search by name or email" aria-label="Search People" onChange={(e) => setQ(e.target.value)} />
      </label>
      <div className="inv-plist">
        {all === null ? (
          <p className="inv-quiet">Loading your people…</p>
        ) : shown.length === 0 ? (
          <p className="inv-quiet">Nobody matches that.</p>
        ) : (
          shown.map((p) => {
            const on = chosen.has(p.email.toLowerCase());
            return (
              <button type="button" key={p.id} className="inv-prow" aria-pressed={on} onClick={() => flip(p)}>
                <Check on={on} />
                <span>
                  <b><bdi>{p.name}</bdi></b>
                  <small>{p.email}</small>
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

function AddedPart({
  text,
  setText,
  preview,
  bad,
  consent,
  setConsent,
}: {
  text: string;
  setText: (t: string) => void;
  preview: InvitePreview | null;
  bad: string[];
  consent: boolean;
  setConsent: (v: boolean) => void;
}) {
  const fresh = preview?.added.fresh ?? [];
  const known = preview?.added.known ?? [];
  const wrong = [...new Set([...bad, ...(preview?.added.bad ?? [])])];
  return (
    <>
      <div className="inv-newbox">
        <textarea dir="auto"
          value={text}
          rows={4}
          spellCheck={false}
          aria-label="New people's emails"
          placeholder={"One a line: jo@example.com, or Jo Park <jo@example.com>"}
          onChange={(e) => setText(e.target.value)}
        />
        {fresh.length || known.length || wrong.length ? (
          <div className="inv-nchips">
            {fresh.map((p) => (
              <span className="inv-nchip ok" key={p.email}>
                {p.name ? `${p.name} · ${p.email}` : p.email}
              </span>
            ))}
            {known.map((p) => (
              <span className="inv-nchip known" key={p.email}>
                {p.name} is already in your people
              </span>
            ))}
            {wrong.map((b) => (
              <span className="inv-nchip bad" key={b}>
                {b}: not an email
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {fresh.length ? (
        <label className="inv-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>
            <b>These {fresh.length === 1 ? "person expects" : `${fmt(fresh.length)} expect`} to hear from me.</b> They get the tag{" "}
            <code>invited</code>, and show in People once they save a seat.
          </span>
        </label>
      ) : null}
    </>
  );
}

// ── the email ───────────────────────────────────────────────────────────────
function TokenRow({ onPut, onRewrite, busy }: { onPut: (tok: string) => void; onRewrite: () => void; busy: boolean }) {
  return (
    <div className="inv-tokens">
      <span>Put in:</span>
      <button type="button" className="inv-tok" onClick={() => onPut("{{first name}}")}>
        {"{{first name}}"}
      </button>
      <button type="button" className="inv-tok" onClick={() => onPut("{{room title}}")}>
        {"{{room title}}"}
      </button>
      <button type="button" className="inv-ai" disabled={busy} onClick={onRewrite}>
        <IconSpark />
        {busy ? "Writing…" : "Write it again"}
      </button>
    </div>
  );
}

function EmailPreview({
  subject,
  note,
  first,
  webinar,
  fromName,
}: {
  subject: string;
  note: string;
  first: string;
  webinar: InviteTarget;
  fromName: string;
}) {
  const values = { firstName: first, webinarTitle: webinar.title };
  const paras = fillText(note, values).split(/\n{2,}/).filter((p) => p.trim());
  const at = webinar.whenLong.indexOf(" · ");
  const day = at < 0 ? webinar.whenLong : webinar.whenLong.slice(0, at);
  const time = at < 0 ? "" : webinar.whenLong.slice(at + 3);
  return (
    <div className="inv-email">
      <div className="inv-esubj">
        <b>{fillText(subject, values)}</b>
        From {fromName}
      </div>
      {paras.map((p, i) => (
        <p key={`${i}-${p.slice(0, 12)}`}>{p}</p>
      ))}
      {webinar.whenLong ? (
        <div className="inv-ewhen">
          <strong>{day}</strong>
          <small>{[time, webinar.length, "free"].filter(Boolean).join(" · ")}</small>
        </div>
      ) : (
        <p className="inv-emuted">Watch whenever you like.</p>
      )}
      <span className="inv-eseat">Save my seat</span>
      <p className="inv-enote">One tap. Your name and email are already filled in.</p>
      <div className="inv-efoot">
        You are receiving this because {fromName} invited you to a room. <u>Unsubscribe</u>
      </div>
    </div>
  );
}
